import { ArrowSquareOut, Storefront } from "@phosphor-icons/react";

/**
 * Renders a rich purchase suggestion card for a material.
 * Handles both new shape (recommended/alternatives/retailers) and legacy flat shape.
 */
export default function PurchaseRecCard({ suggestion, compact = false }) {
  if (!suggestion) return null;

  // Normalise legacy → modern shape on the fly
  const recommended =
    suggestion.recommended || (suggestion.product_type
      ? {
          product_name: suggestion.product_type,
          reasoning: suggestion.reasoning,
          confidence: suggestion.confidence,
          search_url: null,
        }
      : null);
  const alternatives = suggestion.alternatives || [];
  const retailers = suggestion.retailers?.length
    ? suggestion.retailers
    : (suggestion.retailer_category ? [suggestion.retailer_category] : []);

  if (!recommended && retailers.length === 0) return null;

  const conf = recommended?.confidence;
  const showBrand = recommended?.product_name;

  return (
    <div
      className={`bg-kraft border-2 border-stone_border rounded-xl ${
        compact ? "p-3" : "p-4"
      }`}
      data-testid="purchase-rec-card"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="label-cap text-denim">Recommended</p>
        {conf != null && (
          <span className="text-xs font-mono font-bold text-timber-900">
            {Math.round(conf * 100)}% confidence
          </span>
        )}
      </div>

      {showBrand ? (
        <p className="font-heading text-base font-bold text-timber-900 mt-1 leading-snug">
          {recommended.product_name}
        </p>
      ) : (
        <p className="text-sm text-timber-700 mt-1 italic">
          No specific brand recommendation — pick what fits your job.
        </p>
      )}

      {recommended?.reasoning && (
        <p className="text-xs text-timber-700 mt-1 leading-relaxed">
          {recommended.reasoning}
        </p>
      )}

      {retailers.length > 0 && (
        <div className="mt-2 flex items-center gap-1 flex-wrap">
          <Storefront size={14} className="text-timber-700" />
          {retailers.map((r) => (
            <span
              key={r}
              className="text-[11px] font-bold uppercase tracking-wider text-denim bg-chalk border border-stone_border rounded-full px-2 py-0.5"
            >
              {r}
            </span>
          ))}
        </div>
      )}

      {recommended?.search_url && (
        <a
          href={recommended.search_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="purchase-search-link"
          className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-masking border-2 border-masking rounded-lg px-3 py-1.5 btn-press hover:bg-masking hover:text-white transition"
        >
          Find online <ArrowSquareOut size={14} weight="bold" />
        </a>
      )}

      {alternatives.length > 0 && !compact && (
        <div className="mt-3 border-t border-stone_border pt-2">
          <p className="label-cap text-[10px] mb-1">Alternatives</p>
          <ul className="space-y-1.5">
            {alternatives.map((alt, i) => (
              <li key={i} className="text-xs text-timber-900 flex items-center justify-between gap-2">
                <span className="truncate">
                  <span className="font-semibold">{alt.product_name}</span>
                  {alt.reasoning && (
                    <span className="text-timber-500"> · {alt.reasoning}</span>
                  )}
                </span>
                {alt.search_url && (
                  <a
                    href={alt.search_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-masking flex-shrink-0"
                    data-testid={`alt-search-link-${i}`}
                  >
                    <ArrowSquareOut size={14} weight="bold" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
